import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS, TRADEOFF_URL, ASSETS } from "@/lib/site";
import { scrollToSection } from "@/lib/scroll";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (link) => {
    setMenuOpen(false);
    if (link.route) {
      navigate(link.route);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToSection(link.target), 120);
    } else {
      scrollToSection(link.target);
    }
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/90 backdrop-blur-md border-b border-slate-200 shadow-sm"
          : "bg-white/70 backdrop-blur-sm border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-16">
        <div className="flex items-center justify-between h-[76px]">
          <button
            data-testid="logo-home-link"
            onClick={() => handleNav({ target: "home" })}
            className="flex items-center gap-3 group"
            aria-label="Blue Shuck Consulting home"
          >
            <img
              src={ASSETS.wolfNavy}
              alt="Blue Shuck Consulting logo"
              className="h-11 w-11 object-contain transition-transform duration-300 group-hover:scale-105"
            />
            <span className="flex flex-col leading-none text-left">
              <span className="font-heading font-700 text-lg tracking-tight text-brand-navy">
                Blue Shuck
              </span>
              <span className="text-[10px] font-semibold tracking-[0.22em] text-slate-500 uppercase">
                Consulting LTD
              </span>
            </span>
          </button>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map((link) => (
              <button
                key={link.id}
                data-testid={`nav-${link.id}-link`}
                onClick={() => handleNav(link)}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-brand-navy rounded-lg hover:bg-slate-100/70 transition-colors"
              >
                {link.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              data-testid="header-view-tradeoff-cta"
              href={TRADEOFF_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center gap-2 bg-brand-navy text-white text-sm font-medium px-5 py-2.5 rounded-lg hover:bg-brand-navy-dark transition-colors"
            >
              View Trade Off
              <ArrowUpRight className="h-4 w-4" strokeWidth={2} />
            </a>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMenuOpen((v) => !v)}
              className="lg:hidden p-2 rounded-lg text-brand-navy hover:bg-slate-100 transition-colors"
              aria-label="Toggle navigation menu"
            >
              {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-white border-t border-slate-200 px-6 py-4"
        >
          <div className="flex flex-col gap-1">
            {NAV_LINKS.map((link) => (
              <button
                key={link.id}
                data-testid={`mobile-nav-${link.id}-link`}
                onClick={() => handleNav(link)}
                className="text-left px-3 py-3 text-base font-medium text-slate-700 hover:text-brand-navy hover:bg-slate-50 rounded-lg transition-colors"
              >
                {link.label}
              </button>
            ))}
            <a
              data-testid="mobile-view-tradeoff-cta"
              href={TRADEOFF_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex items-center justify-center gap-2 bg-brand-navy text-white font-medium px-5 py-3 rounded-lg hover:bg-brand-navy-dark transition-colors"
            >
              View Trade Off
              <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
